import { useMemo, useState } from 'react'
import { Plus, Search, Trash2 } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import {
  isPartCandidate,
  partsChanged,
  rowsFromParts,
  validateParts,
} from '../lib/stock-usage-parts'
import type { PartRow, PartRowError } from '../lib/stock-usage-parts'
import type { StockUsagePart } from '@/api/endpoints/product-stock'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useProducts } from '@/features/products/hooks/use-products'
import { cn } from '@/lib/utils'

interface StockUsagePartsEditorProps {
  dishSku: string
  /** The saved list; empty when a stock article is being turned into a dish. */
  savedParts: Array<StockUsagePart>
  canEdit: boolean
  isPending: boolean
  /** Whether the product is a dish already (an empty list then removes the usage list). */
  isDish: boolean
  onSave: (parts: Array<{ sku: string; qty: number }>) => void
  /** Leaves the editor without saving (only offered while turning an article into a dish). */
  onCancel?: () => void
}

const GRID =
  'grid grid-cols-[minmax(0,1fr)_120px_90px_110px_40px] items-center gap-3'

/**
 * The parts table of a dish (CHG-2026-09-stock-usage-and-price-twins, 10.3): search stock
 * articles, enter the quantity per sold unit in the part's base unit (de-AT comma, up to 3
 * decimals), see whether the part is tracked, remove it.
 *
 * The "≈ g" weight hint needs the package weight from CHG-2026-09-webbill-delivery-import; this
 * branch has no weight data, so the hint is not rendered.
 */
export function StockUsagePartsEditor({
  dishSku,
  savedParts,
  canEdit,
  isPending,
  isDish,
  onSave,
  onCancel,
}: StockUsagePartsEditorProps) {
  const { t: tProducts } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')
  const [rows, setRows] = useState<Array<PartRow>>(() =>
    rowsFromParts(savedParts),
  )
  const [errors, setErrors] = useState<Record<number, PartRowError>>({})
  const [searchOpen, setSearchOpen] = useState(false)
  const [search, setSearch] = useState('')

  const { data: searchData } = useProducts(
    { search: search || undefined, pageSize: 20, stockArticlesOnly: true },
    { enabled: searchOpen },
  )
  const listedSkus = useMemo(() => new Set(rows.map((r) => r.sku)), [rows])
  const candidates = (searchData?.items ?? []).filter((p) =>
    isPartCandidate(p, dishSku, listedSkus),
  )

  const dirty = partsChanged(rows, savedParts)
  const disabled = !canEdit || isPending

  const updateQty = (index: number, qtyInput: string) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, qtyInput } : row)),
    )
    setErrors((prev) => {
      if (!(index in prev)) return prev
      const next = { ...prev }
      delete next[index]
      return next
    })
  }

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index))
    setErrors({})
  }

  const addRow = (product: {
    sku: string
    name: string
    stockUnit?: string
    stockTracked?: boolean
  }) => {
    setRows((prev) => [
      ...prev,
      {
        sku: product.sku,
        name: product.name,
        unit: product.stockUnit ?? 'Stk',
        stockTracked: product.stockTracked !== false,
        qtyInput: '1',
      },
    ])
    setSearchOpen(false)
    setSearch('')
  }

  const handleSave = () => {
    const result = validateParts(rows, dishSku)
    if (!result.ok) {
      setErrors(result.errors)
      return
    }
    onSave(result.parts)
  }

  const handleDiscard = () => {
    setRows(rowsFromParts(savedParts))
    setErrors({})
    onCancel?.()
  }

  // A plain article saves nothing with an empty list; a dish saving [] becomes an article again.
  const saveDisabled =
    disabled || (!dirty && isDish) || (!isDish && rows.length === 0)

  return (
    <div className="space-y-3">
      <div className="rounded-lg border overflow-hidden">
        <div
          className={cn(
            GRID,
            'bg-muted px-4 py-2 text-xs font-semibold text-muted-foreground',
          )}
        >
          <span>{tProducts('stockUsage.partsArticle')}</span>
          <span className="text-right">{tProducts('stockUsage.partsQty')}</span>
          <span>{tProducts('stockUsage.partsUnit')}</span>
          <span>{tProducts('stockUsage.partsStock')}</span>
          <span />
        </div>

        {rows.length === 0 && (
          <p className="border-t px-4 py-3 text-sm text-muted-foreground">
            {tProducts('stockUsage.partsEmpty')}
          </p>
        )}

        {rows.map((row, index) => {
          const error = errors[index] as PartRowError | undefined
          return (
            <div key={row.sku} className={cn(GRID, 'border-t px-4 py-3')}>
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-medium">{row.name}</span>
                <span className="font-mono text-xs text-muted-foreground">
                  {row.sku}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <Input
                  value={row.qtyInput}
                  onChange={(e) => updateQty(index, e.target.value)}
                  inputMode="decimal"
                  className="text-right"
                  aria-label={tProducts('stockUsage.partsQtyFor', {
                    name: row.name,
                  })}
                  aria-invalid={error ? true : undefined}
                  disabled={disabled}
                />
                {error && (
                  <span className="text-xs text-destructive">
                    {tProducts(`stockUsage.partError.${error}`)}
                  </span>
                )}
              </div>
              <span className="text-sm">{row.unit}</span>
              <span
                className={cn(
                  'text-xs font-medium',
                  row.stockTracked
                    ? 'text-foreground'
                    : 'text-muted-foreground',
                )}
              >
                {row.stockTracked
                  ? tProducts('stockUsage.tracked')
                  : tProducts('stockUsage.notTracked')}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground"
                onClick={() => removeRow(index)}
                disabled={disabled}
                aria-label={tProducts('stockUsage.partsRemove', {
                  name: row.name,
                })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )
        })}

        {canEdit && (
          <div className="border-t bg-muted/40 px-4 py-3">
            <Popover open={searchOpen} onOpenChange={setSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between font-normal text-muted-foreground"
                  disabled={disabled}
                >
                  <span className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    {tProducts('stockUsage.partsAdd')}
                  </span>
                  <Search className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[420px] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder={tProducts('stockUsage.partsSearch')}
                    value={search}
                    onValueChange={setSearch}
                  />
                  <CommandList>
                    <CommandEmpty>{tProducts('combobox.empty')}</CommandEmpty>
                    <CommandGroup>
                      {candidates.map((product) => (
                        <CommandItem
                          key={product.sku}
                          value={product.sku}
                          onSelect={() => addRow(product)}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{product.name}</span>
                            <span className="font-mono text-xs text-muted-foreground">
                              {product.sku}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        {tProducts('stockUsage.partsHint')}
      </p>
      {isDish && rows.length === 0 && dirty && (
        <p className="text-sm text-warning">
          {tProducts('stockUsage.partsRemoveAll')}
        </p>
      )}

      {canEdit && (
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleDiscard}
            disabled={isPending || (!dirty && !onCancel)}
          >
            {onCancel ? tCommon('cancel') : tProducts('stockUsage.discard')}
          </Button>
          <Button type="button" onClick={handleSave} disabled={saveDisabled}>
            {isPending ? tCommon('saving') : tCommon('save')}
          </Button>
        </div>
      )}
    </div>
  )
}
